import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ReadingProgress,
  getReadingProgress,
  upsertReadingProgress,
} from '@/lib/queries/progress';
import { useSession } from './useSession';

export function useReadingProgress() {
  const session = useSession();
  return useQuery<ReadingProgress | null, Error>({
    queryKey: ['reading_progress', session.data?.user.id ?? null],
    queryFn: getReadingProgress,
    enabled: !!session.data,
  });
}

export function useTrackReadingProgress() {
  const session = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { surahId: number; ayahNumber: number }) => {
      const user = session.data?.user;
      if (!user) throw new Error('Not signed in');
      return upsertReadingProgress({
        user_id: user.id,
        last_surah_id: input.surahId,
        last_ayah_number: input.ayahNumber,
      });
    },
    onSuccess: (_data, vars) => {
      const userId = session.data?.user.id;
      if (!userId) return;
      queryClient.setQueryData<ReadingProgress | null>(
        ['reading_progress', userId],
        {
          user_id: userId,
          last_surah_id: vars.surahId,
          last_ayah_number: vars.ayahNumber,
          updated_at: new Date().toISOString(),
        },
      );
    },
  });
}

/**
 * Side-effect hook for the surah reader. When a signed-in user opens a
 * surah, record progress at (surahId, ayahNumber ?? 1). Deduped per
 * (user, surah, ayah) so the network call happens once even if React
 * re-mounts the component. Resets the dedupe key when the user changes
 * (sign-in / sign-out / different user) so a new identity gets its own
 * first write.
 */
export function useRecordSurahOpen(
  surahId: number | null,
  ayahNumber: number | null,
) {
  const session = useSession();
  const track = useTrackReadingProgress();
  const lastSentRef = useRef<{ userId: string | null; key: string | null }>({
    userId: null,
    key: null,
  });

  useEffect(() => {
    const userId = session.data?.user.id ?? null;
    if (!userId || !surahId) {
      lastSentRef.current = { userId, key: null };
      return;
    }
    const key = `${surahId}:${ayahNumber ?? 1}`;
    if (
      lastSentRef.current.userId === userId &&
      lastSentRef.current.key === key
    ) {
      return;
    }
    lastSentRef.current = { userId, key };
    track.mutate({ surahId, ayahNumber: ayahNumber ?? 1 });
    // `track` is intentionally excluded — TanStack Query mutation objects
    // are referentially fresh on every render, which would loop the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.data?.user.id, surahId, ayahNumber]);
}
